import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { reasonLabel } from '../lib/reports';

interface Report {
  id: number;
  created_at: string;
  test_id: string;
  reason: string;
  details: string | null;
  reporter_contact: string | null;
  block_requested: boolean;
  status: 'new' | 'reviewing' | 'resolved' | 'dismissed';
  admin_note: string | null;
  partner_name: string | null;
  partner_platform: string | null;
  session_created_at: string | null;
  creator_email: string | null;
  reports_on_session: number;
}

type Filter = 'open' | 'closed' | 'all';

const timeAgo = (iso: string) => {
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const STATUS_STYLE: Record<Report['status'], { label: string; cls: string }> = {
  new:       { label: 'New',       cls: 'bg-rose-500/15 text-rose-400 border-rose-500/30' },
  reviewing: { label: 'Reviewing', cls: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
  resolved:  { label: 'Resolved',  cls: 'bg-emerald-500/15 text-emerald-500 border-emerald-500/30' },
  dismissed: { label: 'Dismissed', cls: 'bg-zinc-500/15 text-zinc-500 border-zinc-500/30' },
};

export default function AdminReports({ onNewCount }: { onNewCount: (n: number) => void }) {
  const [reports, setReports] = useState<Report[] | null>(null);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<Filter>('open');
  const [notes, setNotes] = useState<Record<number, string>>({});
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = async () => {
    const { data, error } = await supabase.rpc('admin_list_reports', { p_status: null, p_limit: 200 });
    if (error) { setError(error.message || 'Could not load reports.'); return; }
    setError('');
    const list = (data as Report[]) || [];
    setReports(list);
    onNewCount(list.filter(r => r.status === 'new').length);
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (r: Report, status: Report['status']) => {
    setBusyId(r.id);
    const { error } = await supabase.rpc('admin_update_report', { p_id: r.id, p_status: status, p_note: notes[r.id] || null });
    if (error) alert(error.message);
    setBusyId(null);
    await load();
  };

  if (error) {
    return (
      <div className="bg-brand-card border border-brand-border rounded-2xl p-6 text-sm">
        <div className="font-bold text-white mb-2">Reports aren't set up yet</div>
        <p className="text-zinc-500 leading-relaxed mb-3">
          Open Supabase → SQL Editor, paste the contents of <code>supabase/reports.sql</code> (it's in your GitHub repo) and press Run. Then refresh this page.
        </p>
        <p className="text-[11px] text-zinc-600 break-words">Details: {error}</p>
      </div>
    );
  }
  if (!reports) return <div className="text-center py-16 text-zinc-600 text-sm">Loading…</div>;

  const isOpen = (r: Report) => r.status === 'new' || r.status === 'reviewing';
  const shown = reports.filter(r => filter === 'all' ? true : filter === 'open' ? isOpen(r) : !isOpen(r));

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        {([['open', 'Open'], ['closed', 'Closed'], ['all', 'All']] as [Filter, string][]).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={`px-4 py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest cursor-pointer transition-all ${
              filter === key ? 'bg-brand-accent text-white' : 'bg-brand-card border border-brand-border text-zinc-500 hover:text-white'
            }`}
          >
            {label}{key === 'open' ? ` (${reports.filter(isOpen).length})` : ''}
          </button>
        ))}
      </div>

      {shown.length === 0 && (
        <div className="text-center py-14 text-zinc-600 text-sm">
          {filter === 'open' ? 'No open reports. 🎉' : 'Nothing here.'}
        </div>
      )}

      {shown.map(r => {
        const st = STATUS_STYLE[r.status];
        return (
          <div key={r.id} className="bg-brand-card border border-brand-border rounded-2xl p-5 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full border ${st.cls}`}>{st.label}</span>
              <span className="text-sm font-bold text-white">{reasonLabel(r.reason)}</span>
              <span className="text-[11px] text-zinc-600 ml-auto">{timeAgo(r.created_at)}</span>
            </div>

            {(r.block_requested || r.reports_on_session > 1) && (
              <div className="flex flex-wrap gap-2">
                {r.block_requested && (
                  <span className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full border bg-amber-500/15 text-amber-400 border-amber-500/30">
                    Asked to stop messages
                  </span>
                )}
                {r.reports_on_session > 1 && (
                  <span className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full border bg-rose-500/15 text-rose-400 border-rose-500/30">
                    {r.reports_on_session} reports on this session
                  </span>
                )}
              </div>
            )}

            {r.details && (
              <p className="text-sm text-zinc-400 leading-relaxed border-l-2 border-brand-border pl-3 whitespace-pre-wrap break-words">
                {r.details}
              </p>
            )}

            <div className="text-xs text-zinc-500 space-y-1 break-words">
              {r.reporter_contact && (
                <div>Reporter: <span className="text-zinc-300 font-semibold">{r.reporter_contact}</span></div>
              )}
              <div>
                Session for <span className="text-zinc-300 font-semibold">{r.partner_name || 'unknown'}</span>
                {r.partner_platform ? ` on ${r.partner_platform}` : ''}
                {r.session_created_at ? ` · sent ${new Date(r.session_created_at).toLocaleDateString()}` : ''}
              </div>
              <div>Sender: <span className="text-zinc-300 font-semibold">{r.creator_email || 'unknown (session deleted?)'}</span></div>
              <div className="font-mono text-[10px] text-zinc-600 select-all">{r.test_id}</div>
            </div>

            {r.admin_note && (
              <div className="text-xs text-zinc-500">Your note: <span className="text-zinc-300">{r.admin_note}</span></div>
            )}

            <textarea
              value={notes[r.id] ?? ''}
              onChange={e => setNotes(n => ({ ...n, [r.id]: e.target.value }))}
              placeholder="Add a note (optional)"
              rows={2}
              maxLength={1000}
              className="w-full bg-brand-bg border border-brand-border rounded-xl px-3 py-2 text-base text-zinc-300 placeholder:text-zinc-600 outline-none resize-none"
            />

            <div className="flex flex-wrap gap-2">
              {isOpen(r) ? (
                <>
                  {r.status === 'new' && (
                    <button disabled={busyId === r.id} onClick={() => setStatus(r, 'reviewing')}
                      className="px-4 py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest cursor-pointer bg-brand-card border border-brand-border text-amber-400 disabled:opacity-50">
                      Reviewing
                    </button>
                  )}
                  <button disabled={busyId === r.id} onClick={() => setStatus(r, 'resolved')}
                    className="px-4 py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest cursor-pointer bg-brand-accent text-white disabled:opacity-50">
                    Resolve
                  </button>
                  <button disabled={busyId === r.id} onClick={() => setStatus(r, 'dismissed')}
                    className="px-4 py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest cursor-pointer bg-brand-card border border-brand-border text-zinc-500 disabled:opacity-50">
                    Dismiss
                  </button>
                </>
              ) : (
                <button disabled={busyId === r.id} onClick={() => setStatus(r, 'reviewing')}
                  className="px-4 py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest cursor-pointer bg-brand-card border border-brand-border text-zinc-500 disabled:opacity-50">
                  Reopen
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
